/**
 * 表格列工具函数
 * 提供格式化、颜色处理等通用方法
 */

import { TableColumn } from './courseConfig'

/**
 * 敏感信息打码 - 股票名称
 * 保留首字，其他字符使用*
 * 示例：浦发银行 -> 浦***
 */
export function maskStockName(name: string | null | undefined): string {
    if (!name) return '--'
    if (name.length <= 1) return name
    return name[0] + '*'.repeat(name.length - 1)
}

/**
 * 敏感信息打码 - 股票代码
 * 保留首字和尾字，其他字符使用*
 * 示例：600000 -> 6****0
 */
export function maskStockCode(code: string | null | undefined): string {
    if (!code) return '--'
    if (code.length <= 2) return code
    return code[0] + '*'.repeat(code.length - 2) + code[code.length - 1]
}

/**
 * 根据 mcnMode 参数判断是否需要打码
 * @param mcnMode 路由参数，1-启用打码，0-不启用（默认为0）
 */
export function isMaskEnabled(mcnMode: string | null | undefined): boolean {
    return mcnMode === '1'
}

/**
 * 格式化数字（保留2位小数）
 */
export function formatNumber(val: number | null | undefined): string {
    if (val === null || val === undefined) return '--'
    return val.toFixed(2)
}

/**
 * 格式化百分比
 */
export function formatPercent(val: number | null | undefined): string {
    if (val === null || val === undefined) return '--'
    const percent = val * 100
    return percent.toFixed(2) + '%'
}

/**
 * 格式化金额（亿/万）
 */
export function formatAmount(val: number | null | undefined): string {
    if (val === null || val === undefined) return '--'
    const absVal = Math.abs(val)
    const sign = val < 0 ? '-' : ''

    if (absVal >= 100000000) {
        return sign + (absVal / 100000000).toFixed(2) + '亿'
    }
    if (absVal >= 10000) {
        return sign + (absVal / 10000).toFixed(2) + '万'
    }
    return sign + absVal.toFixed(0)
}

/**
 * 根据涨跌获取价格样式类名
 */
export function getPriceClass(val: number | null | undefined): string {
    if (val === null || val === undefined) return ''
    if (val > 0) return 'price-up'
    if (val < 0) return 'price-down'
    return 'price-zero'
}

/**
 * 根据格式化类型格式化值
 */
export function formatValue(val: any, formatter?: string): string {
    switch (formatter) {
        case 'number':
            return formatNumber(val)
        case 'percent':
            return formatPercent(val)
        case 'amount':
            return formatAmount(val)
        case 'text':
        default:
            return val ?? '--'
    }
}

/**
 * 获取列样式
 */
export function getColumnStyle(column: TableColumn): Record<string, string> {
    const style: Record<string, string> = {}

    if (column.width) {
        style.width = typeof column.width === 'number' ? `${column.width}px` : column.width
    }

    if (column.color) {
        style.color = column.color
    }

    return style
}

/**
 * 获取列对齐类名
 */
export function getAlignClass(align?: string): string {
    switch (align) {
        case 'left':
            return 'th-left'
        case 'center':
            return 'th-center'
        case 'right':
            return 'th-right'
        default:
            return 'th-left'
    }
}

/**
 * 生成 colgroup 宽度数组
 */
export function generateColWidths(columns: TableColumn[]): (number | string)[] {
    return columns.map(col => col.width)
}

/**
 * 获取固定列的 left 值
 */
export function getFixedLeftValue(columns: TableColumn[], currentIndex: number): number {
    let left = 0
    for (let i = 0; i < currentIndex; i++) {
        const col = columns[i]
        if (col.fixed === 'left') {
            left += typeof col.width === 'number' ? col.width : parseInt(col.width as string, 10) || 0
        }
    }
    return left
}

export default {
    maskStockName,
    maskStockCode,
    isMaskEnabled,
    formatNumber,
    formatPercent,
    formatAmount,
    getPriceClass,
    formatValue,
    getColumnStyle,
    getAlignClass,
    generateColWidths,
    getFixedLeftValue
}
